import { useCallback, useMemo } from "react"
import { useRouter } from "next/router"

import { useAgentsById } from "@/agents/catalog"
import type { MetricRecord } from "@/metrics/runtime"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import type { SofiaPageSummary } from "@/lib/covi/sofiaPageSummary"
import { openCoviWithSummary } from "@/lib/coviEvents"
import { useCoviSummaryMemory } from "@/hooks/useCoviSummaryMemory"
import { useRefreshingReferenceDate } from "@/hooks/useRefreshingReferenceDate"
import { usePublishSofiaPageSummary } from "@/hooks/useSofiaPageSummary"

import { useConversationsFilter } from "../contexts/ConversationsFilterContext"
import {
  useConversationsSummaryRowsTransport,
  type ConversationsTransportFilters,
} from "../transport"
import {
  toConversationsAnalysisWindowDays,
  toConversationsSummaryTimeframe,
} from "../utils/conversationsDateWindow"
import { MAX_KEYSET_SIMULATION_OUTPUT_IDS } from "../utils/conversationsProjectFilter"
import { buildConversationsPageSummary } from "../utils/conversationsSummaryBuilder"
import { buildConversationsPageSummaryMarkdown } from "../utils/conversationsSummaryMarkdown"
import { buildConversationsTransportFilters } from "../utils/conversationsTransportFilters"
import { useConversationsSimulationOutputRestriction } from "./useConversationsSimulationOutputRestriction"

const SUMMARY_TITLE = "Conversations Summary"

interface UseConversationsSummaryPublisherParams {
  enabled?: boolean
  metricDisplayNames?: Record<string, string>
  metricRecordsById?: Record<string, MetricRecord | undefined>
}

function buildConversationsSummaryFilters(
  filters: ConversationsTransportFilters,
): ConversationsTransportFilters {
  return {
    ...filters,
    // The summary timeframe owns its current/prior boundaries. Carrying the
    // table date range would silently truncate the comparison window.
    start_date: undefined,
    end_date: undefined,
  }
}

/**
 * Publishes the conversations page's narrative summary to Sofia, which opens the
 * chat on it as one of her messages when the user arrives.
 *
 * The page body renders nothing for it. The window follows the table's date
 * filter, falling back to the one the backend picks adaptively from
 * conversation volume. Nothing is published while it is still loading, so Sofia
 * never presents a summary that would open empty.
 */
export function useConversationsSummaryPublisher({
  enabled = true,
  metricDisplayNames,
  metricRecordsById,
}: UseConversationsSummaryPublisherParams): void {
  const { trackEvent } = useAnalytics()
  const router = useRouter()
  const summaryReferenceDate = useRefreshingReferenceDate(enabled)
  const {
    state: {
      agentIds,
      dateRange,
      metadataFilters,
      metricIds,
      metricValueFilters,
      projectIds,
      tagFilters,
    },
  } = useConversationsFilter()
  const filters = useMemo(
    () =>
      buildConversationsSummaryFilters(
        buildConversationsTransportFilters({
          agentIds,
          dateRange,
          metadataFilters,
          metricIds,
          metricValueFilters,
          tagFilters,
        }),
      ),
    [
      agentIds,
      dateRange,
      metadataFilters,
      metricIds,
      metricValueFilters,
      tagFilters,
    ],
  )
  // Sofia describes the window the table is showing. Unfiltered, or filtered to
  // a range that is not one of the window pills, the backend picks adaptively.
  const summaryTimeframe = toConversationsSummaryTimeframe(dateRange)
  const restriction = useConversationsSimulationOutputRestriction({ enabled })
  const summaryEnabled =
    enabled &&
    !restriction.isPending &&
    !restriction.isError &&
    !restriction.matchesNothing
  const {
    data: summaryRows,
    isError,
    isLoading,
    outcomeInsights,
  } = useConversationsSummaryRowsTransport({
    analysisWindowDays: toConversationsAnalysisWindowDays(summaryTimeframe),
    enabled: summaryEnabled,
    filters,
    simulationOutputIds: restriction.simulationOutputIds,
  })
  const organizationSlug =
    typeof router.query.slug === "string" ? router.query.slug : null
  const { summaryPreferencesText } = useCoviSummaryMemory(organizationSlug)
  const { agentsById } = useAgentsById({ enabled })
  const agentNames = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(agentsById).map(([agentId, agent]) => [
          agentId,
          agent.display_name || agentId.slice(0, 8),
        ]),
      ),
    [agentsById],
  )
  const summaryIsLoading = isLoading || !summaryEnabled
  const summary = useMemo(
    () =>
      buildConversationsPageSummary(summaryTimeframe, summaryReferenceDate, {
        focusText: summaryPreferencesText,
        outcomeInsights,
        rows: summaryRows,
        lookups: {
          agentNames,
          metricDisplayNames,
          metricRecordsById,
        },
      }),
    [
      agentNames,
      metricDisplayNames,
      metricRecordsById,
      outcomeInsights,
      summaryPreferencesText,
      summaryReferenceDate,
      summaryRows,
      summaryTimeframe,
    ],
  )
  // Over the request cap the summary covers only the most recently linked
  // conversations, which the table discloses too; say so here as well.
  const projectTruncatedFromCount = restriction.truncatedFromCount
  const summaryMarkdown = useMemo(() => {
    const markdown = buildConversationsPageSummaryMarkdown(summary)
    if (markdown.trim().length === 0 || projectTruncatedFromCount === null) {
      return markdown
    }
    return `${markdown}\n\n_Covers the ${MAX_KEYSET_SIMULATION_OUTPUT_IDS.toLocaleString()} conversations most recently added to the selected projects, out of ${projectTruncatedFromCount.toLocaleString()}._`
  }, [projectTruncatedFromCount, summary])

  const handlePresentSummary = useCallback(() => {
    trackEvent(
      TRACKING_EVENTS.UPLOADED_CONVERSATIONS
        .CONVERSATIONS_SUMMARY_DISCUSS_WITH_COVI,
      {
        selected_timeframe: summary.selectedTimeframe,
        resolved_timeframe: summary.resolvedTimeframe,
        summary_length: summary.summaryText.length,
        total_conversations: summary.currentStats.totalConversations,
        status_known_conversations:
          summary.currentStats.statusKnownConversations,
        completed_conversations: summary.currentStats.completedConversations,
        unsuccessful_conversations:
          summary.currentStats.unsuccessfulConversations,
        assistant_brand: "sofia",
      },
      { includeIdentity: false },
    )

    openCoviWithSummary({ title: SUMMARY_TITLE, markdown: summaryMarkdown })
  }, [summary, summaryMarkdown, trackEvent])

  // The resolved timeframe and the selected projects are part of the identity:
  // either change produces a summary about different conversations, which is a
  // summary the user has not been shown yet.
  const sourceId = useMemo(() => {
    const timeframeSourceId = `conversations:${summary.resolvedTimeframe}`
    return projectIds.length === 0
      ? timeframeSourceId
      : `${timeframeSourceId}:projects=${[...projectIds].sort().join(",")}`
  }, [projectIds, summary.resolvedTimeframe])
  const pageSummary = useMemo<SofiaPageSummary | null>(
    () =>
      summaryIsLoading || isError || summaryMarkdown.trim().length === 0
        ? null
        : {
            title: SUMMARY_TITLE,
            markdown: summaryMarkdown,
            sourceId,
            onPresent: handlePresentSummary,
          },
    [
      handlePresentSummary,
      isError,
      sourceId,
      summaryIsLoading,
      summaryMarkdown,
    ],
  )
  usePublishSofiaPageSummary(pageSummary)
}
